// Entry point. Starts the console's half of the sign-in (auth.ts), then mounts.

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app.js";
import { initAuth } from "./auth.js";
import { readEnv } from "./env.js";
import "./styles/console.css";

const root = createRoot(document.getElementById("root") as HTMLElement);

// An async function rather than top-level `await`. Top-level await compiles only
// for an es2022+ build target, and raising Vite's default target to get it would
// quietly drop support for browsers nobody has checked this app in — a real
// decision, taken by accident, to buy three characters of syntax.
async function boot(): Promise<void> {
  const env = readEnv();
  // `notice` is a FAILED sign-in return leg that did not stop the app from
  // booting — a stale link, a mismatched state, a refusal from Auth0. It is
  // handed to <App/> and shown above the Sign in button. Nothing here needs to
  // branch on it: initAuth cannot throw for those cases any more, which is the
  // whole point (see auth.ts, and apps/fe/src/auth-callback-classify.js).
  const { session, notice } = initAuth();
  root.render(
    <StrictMode>
      <App session={session} apiBase={env.apiBase} notice={notice} />
    </StrictMode>,
  );
}

void boot().catch((err: unknown) => {
  // ⚠️ A boot failure must SAY SO on the page. Logging it to the console and
  // rendering nothing gives a blank white screen — indistinguishable from a
  // hosting problem, which is exactly the wrong place to start looking when the
  // real cause is in the app itself.
  const message = err instanceof Error ? err.message : String(err);
  root.render(
    <div style={{ padding: 28, fontFamily: "var(--font-sans)", maxWidth: 620 }}>
      <div className="pill error" style={{ marginBottom: 12 }}>
        The console could not start
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>{message}</p>
      {/*
        ★ A WAY OUT. This screen replaces the entire app, so whatever it shows is
        the only thing on the page — and until 2026-08-25 that was a sentence and
        nothing else. Omar hit it on staging and recovered by guessing that a
        refresh might work; the page never said so.

        Boot now builds no Auth0 client (#385) and reads no build setting, so no
        known cause reaches this screen; Try again stays for the fault nobody has
        found yet.
        What used to arrive here and genuinely could NOT be fixed by reloading —
        a stale callback replaying forever — no longer arrives here at all.
      */}
      <button
        type="button"
        className="btn primary focusable"
        style={{ marginTop: 16 }}
        onClick={() => window.location.reload()}
      >
        Try again
      </button>
    </div>,
  );
  // Re-throw so the failure also reaches the browser console and any error
  // reporting, rather than being swallowed by having been handled visually.
  throw err;
});
